import type { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invalidateScoutingData } from "@/lib/scoutingDataInvalidation";
import { invalidateTreatmentData } from "@/lib/treatmentDataInvalidation";

export interface LinkedSprayLog {
  id: string;
  date: string | null;
}

/**
 * Returns linked spray log(s) for a task, if any.
 * Used to upgrade the delete-confirmation warning before the user confirms.
 */
export async function getLinkedSprayLogsForTask(
  taskId: string
): Promise<LinkedSprayLog[]> {
  const { data, error } = await supabase
    .from("spray_logs")
    .select("id, date")
    .eq("task_id", taskId);
  if (error) {
    console.error("getLinkedSprayLogsForTask error:", error);
    return [];
  }
  return (data || []) as LinkedSprayLog[];
}

/**
 * Same as {@link getLinkedSprayLogsForTask} but for a batch of tasks, in ONE query.
 * Used by the bulk-delete confirmation to warn that deleting will revert inventory.
 * Returns a map task_id → its spray logs (tasks with none are absent from the map).
 */
export async function getLinkedSprayLogsForTasks(
  taskIds: string[]
): Promise<Map<string, LinkedSprayLog[]>> {
  const byTask = new Map<string, LinkedSprayLog[]>();
  if (taskIds.length === 0) return byTask;
  const { data, error } = await supabase
    .from("spray_logs")
    .select("id, date, task_id")
    .in("task_id", taskIds);
  if (error) {
    console.error("getLinkedSprayLogsForTasks error:", error);
    return byTask;
  }
  for (const row of data || []) {
    if (!row.task_id) continue;
    const list = byTask.get(row.task_id) || [];
    list.push({ id: row.id, date: row.date });
    byTask.set(row.task_id, list);
  }
  return byTask;
}

/**
 * Qué partes vuelven a abrirse al BORRAR una tarea. Solo un tratamiento HECHO
 * resolvió algo: borrarlo reabre todos sus partes (la plaga cuenta otra vez como
 * activa). Uno que NO se hizo no puede reabrir lo que alguien cerró por su cuenta
 * («Cerrar foco · Fue un error» y luego borrar el tratamiento que sobraba lo
 * reabría: ensayos T1/T1b del 23-09-2026); solo vuelven a «pendiente» los que
 * esperaban por él. Los que ya están pendientes no hace falta tocarlos.
 */
export function sessionsToReopen(
  task: { status: string | null | undefined; actual_end_time?: string | null } | null | undefined,
  sessions: readonly { id: string; review_status: string | null; resolved_at?: string | null }[],
): string[] {
  const done = task?.status === "completed";
  // Lo que resolvió ESTE tratamiento: resuelto desde que se completó (60 s de margen por
  // relojes) o sin sello (anterior al 21-09-2026). Un cierre a mano ANTES no es suyo.
  const since = done && task?.actual_end_time ? Date.parse(task.actual_end_time) - 60_000 : null;
  const suyo = (s: { resolved_at?: string | null }) => since == null || !s.resolved_at || Date.parse(s.resolved_at) >= since;
  return sessions
    .filter((s) => (s.review_status === "resolved" ? done && suyo(s) : true))
    .filter((s) => s.review_status !== "pending")
    .map((s) => s.id);
}

/**
 * Por qué no se pudo borrar una tarea, con un código que la pantalla puede
 * traducir. Los códigos los lanza `delete_planner_task` en la base de datos.
 */
export type TaskDeleteCode = "PERMISSION_DENIED" | "TASK_NOT_FOUND" | "TRANSACTIONS_STILL_LINKED" | "UNKNOWN";

export class TaskDeleteError extends Error {
  constructor(public readonly code: TaskDeleteCode, message: string) {
    super(message);
    this.name = "TaskDeleteError";
  }
}

const TASK_DELETE_CODES: TaskDeleteCode[] = ["PERMISSION_DENIED", "TASK_NOT_FOUND", "TRANSACTIONS_STILL_LINKED"];

export function taskDeleteCode(error: unknown): TaskDeleteCode {
  if (error instanceof TaskDeleteError) return error.code;
  const text = String((error as { message?: unknown } | null)?.message ?? error ?? "");
  return TASK_DELETE_CODES.find((c) => text.includes(c)) ?? "UNKNOWN";
}

/** Clave i18n del aviso de error al borrar una tarea (todas bajo `planner.`). */
export function taskDeleteErrorKey(error: unknown): string {
  switch (taskDeleteCode(error)) {
    case "PERMISSION_DENIED":
      return "planner.deleteTaskNoPermission";
    case "TASK_NOT_FOUND":
      return "planner.deleteTaskAlreadyGone";
    default:
      return "planner.errorDeletingTask";
  }
}

/**
 * Lo que cambia al borrar una tarea, para que ninguna pantalla se quede
 * enseñando lo de antes: sus partes y su coste (panel, analíticas, informes),
 * el stock que vuelve (inventario) y los partes de plaga que se reabren.
 */
export function invalidateAfterTaskDelete(queryClient: QueryClient) {
  invalidateTreatmentData(queryClient);
  invalidateScoutingData(queryClient);
  for (const key of ["inventory", "inventory-location-stock", "tasks-impacted-stock", "inventory-linked-task", "inventory-value-history"]) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
}

/**
 * Borra una tarea del planificador DE UNA VEZ.
 *
 * Todo lo que cuelga de ella —movimientos de stock de sus partes (al borrarlos,
 * el trigger devuelve el stock a la finca y al total), líneas, partes y la propia
 * tarea— se borra en UNA transacción en la base de datos (`delete_planner_task`,
 * migración 20260924170000): o todo o nada.
 *
 * Antes eran tres viajes desde el navegador (soltar el parte, llamar a
 * `delete-spray-log-secure`, borrar la tarea) y el último lo decidía la RLS, que
 * solo dejaba borrar tareas al ADMINISTRADOR. Un DELETE que la RLS no deja pasar
 * no da error —borra cero filas—, así que a un ENCARGADO se le decía «Tarea
 * eliminada» y la tarea volvía; y si estaba completada, sus partes ya se habían
 * borrado y el stock devuelto, pero la tarea se quedaba completada y sin parte
 * (20 tareas así en Frutas Nijasol, 18–24-09-2026). Quién puede borrar lo decide
 * ahora UNA regla, `can_delete_planner_task`, la misma para la función y para la
 * RLS: administrador, encargado en sus fincas, perito lo que creó él.
 *
 * Los partes de plaga: con focos los reabre el trigger `pt_focus_task_delete`;
 * sin focos, aquí, y solo lo que esta tarea resolvió (`sessionsToReopen`).
 *
 * Lanza `TaskDeleteError` (con `code`) si no se pudo; `taskDeleteErrorKey` da el
 * aviso. Con `queryClient`, refresca todo lo que depende de la tarea.
 */
export async function deleteTaskWithCascade(taskId: string, queryClient?: QueryClient): Promise<void> {
  // 1. Qué había colgado de la tarea, leído ANTES de borrarla: al borrarla, la FK
  //    suelta los partes y ya no se sabría cuáles eran (también los enlazados solo
  //    por linked_task_id).
  const { data: taskRow } = await supabase
    .from("planner_tasks")
    .select("status, actual_end_time")
    .eq("id", taskId)
    .maybeSingle();
  const { data: sessions } = await supabase
    .from("pest_scouting_sessions")
    .select("id, review_status, resolved_at")
    .eq("linked_task_id", taskId);

  // 2. La tarea y todo lo suyo, en una sola transacción.
  const { error } = await supabase.rpc("delete_planner_task", { p_task_id: taskId });
  if (error) {
    const code = taskDeleteCode(error);
    throw new TaskDeleteError(code, error.message || code);
  }

  // 3. Reabrir solo lo que esta tarea resolvió (`sessionsToReopen`). Con focos
  //    encendidos el trigger ya lo ha hecho; esto cubre al resto y no cambia nada
  //    si ya está así.
  const reopen = sessionsToReopen(taskRow, sessions ?? []);
  if (reopen.length > 0) {
    await supabase
      .from("pest_scouting_sessions")
      .update({ review_status: "pending" })
      .in("id", reopen);
  }

  if (queryClient) invalidateAfterTaskDelete(queryClient);
}
